import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen pt-20" data-testid="privacy-page">
      <Helmet>
        <title>Politique de Confidentialité | École de Musique Pour Tous - Périgueux</title>
        <meta name="description" content="Politique de confidentialité et protection des données personnelles de l'École de Musique Pour Tous à Périgueux. RGPD compliant." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://ecoledemusiqueperigueux.fr/confidentialite" />
      </Helmet>
      <section className="py-16 bg-cream">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-dancing text-5xl text-burgundy mb-8">
              Politique de confidentialité
            </h1>

            <div className="bg-white rounded-3xl shadow-sm p-8 space-y-8 font-nunito">
              <section>
                <h2 className="font-bold text-xl text-burgundy mb-4">1. Introduction</h2>
                <p className="text-gray-700 leading-relaxed">
                  L'École de Musique Pour Tous, en tant que responsable du traitement, attache une grande importance 
                  à la protection et au respect de votre vie privée. La présente politique vise à vous 
                  informer de nos pratiques concernant la collecte, l'utilisation et le partage des 
                  informations que vous êtes amenés à nous fournir via ce site.
                </p>
              </section>

              <section>
                <h2 className="font-bold text-xl text-burgundy mb-4">2. Données collectées</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Lors de votre utilisation de notre site, nous sommes susceptibles de collecter les 
                  informations suivantes :
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Nom et prénom</li>
                  <li>Adresse email</li>
                  <li>Numéro de téléphone</li>
                  <li>Âge de l'élève concerné</li>
                  <li>Instrument ou activité souhaité(e)</li>
                  <li>Niveau musical</li>
                  <li>Disponibilités</li>
                  <li>Message libre</li>
                </ul>
              </section>

              <section>
                <h2 className="font-bold text-xl text-burgundy mb-4">3. Finalités du traitement</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Les données personnelles collectées ont pour finalités :
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Répondre à vos demandes de renseignements</li>
                  <li>Organiser des cours d'essai</li>
                  <li>Vous recontacter pour la gestion de votre inscription</li>
                  <li>Assurer le suivi de votre parcours musical</li>
                </ul>
              </section>

              <section>
                <h2 className="font-bold text-xl text-burgundy mb-4">4. Base légale</h2>
                <p className="text-gray-700 leading-relaxed">
                  Le traitement de vos données est fondé sur votre consentement, que vous exprimez en 
                  soumettant le formulaire de contact.
                </p>
              </section>

              <section>
                <h2 className="font-bold text-xl text-burgundy mb-4">5. Destinataires des données</h2>
                <p className="text-gray-700 leading-relaxed">
                  Vos données personnelles sont destinées exclusivement à l'École de Musique Pour Tous et ne sont 
                  en aucun cas communiquées à des tiers, sauf obligation légale.
                </p>
              </section>

              <section>
                <h2 className="font-bold text-xl text-burgundy mb-4">6. Durée de conservation</h2>
                <p className="text-gray-700 leading-relaxed">
                  Vos données sont conservées pendant une durée de 3 ans à compter de votre dernier 
                  contact avec nous, ou jusqu'à la demande de suppression de votre part.
                </p>
              </section>

              <section>
                <h2 className="font-bold text-xl text-burgundy mb-4">7. Vos droits</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Conformément au RGPD, vous disposez des droits suivants :
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li><strong>Droit d'accès :</strong> obtenir des informations sur vos données</li>
                  <li><strong>Droit de rectification :</strong> demander la correction de données inexactes</li>
                  <li><strong>Droit à l'effacement :</strong> demander la suppression de vos données</li>
                  <li><strong>Droit à la limitation :</strong> restreindre le traitement de vos données</li>
                  <li><strong>Droit à la portabilité :</strong> recevoir vos données dans un format structuré</li>
                  <li><strong>Droit d'opposition :</strong> vous opposer au traitement de vos données</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  Pour exercer ces droits, vous pouvez nous contacter à l'adresse : 
                  <a href="mailto:musiquepourtous24@gmail.com" className="text-burgundy hover:underline ml-1">
                    musiquepourtous24@gmail.com
                  </a>
                </p>
              </section>

              <section>
                <h2 className="font-bold text-xl text-burgundy mb-4">8. Sécurité</h2>
                <p className="text-gray-700 leading-relaxed">
                  Nous mettons en œuvre des mesures de sécurité appropriées pour protéger vos données 
                  contre tout accès non autorisé, modification, divulgation ou destruction.
                </p>
              </section>

              <section>
                <h2 className="font-bold text-xl text-burgundy mb-4">9. Cookies</h2>
                <p className="text-gray-700 leading-relaxed">
                  Pour plus d'informations sur l'utilisation des cookies sur ce site, veuillez consulter 
                  notre <Link to="/cookies" className="text-burgundy hover:underline">politique de cookies</Link>.
                </p>
              </section>

              <section>
                <h2 className="font-bold text-xl text-burgundy mb-4">10. Modifications</h2>
                <p className="text-gray-700 leading-relaxed">
                  Nous nous réservons le droit de modifier cette politique de confidentialité à tout 
                  moment. Les modifications entrent en vigueur dès leur publication sur le site.
                </p>
              </section>

              <section>
                <h2 className="font-bold text-xl text-burgundy mb-4">11. Contact</h2>
                <p className="text-gray-700 leading-relaxed">
                  Pour toute question concernant cette politique de confidentialité, vous pouvez nous 
                  contacter :<br /><br />
                  <strong>École de Musique Pour Tous</strong><br />
                  2 Rue Ludovic Trarieux, 24000 Périgueux<br />
                  Email : <a href="mailto:musiquepourtous24@gmail.com" className="text-burgundy hover:underline">musiquepourtous24@gmail.com</a><br />
                  Téléphone : <a href="tel:+33649523778" className="text-burgundy hover:underline">06 49 52 37 78</a>
                </p>
              </section>

              <p className="text-gray-500 text-sm pt-4 border-t">
                Dernière mise à jour : Janvier 2026
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
